import React, { useEffect, useState } from 'react';
import { X, Gem, Ruler, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import WhatsAppIcon from './WhatsAppIcon';
import { waLink, products, waLinkProps } from '../mock';
import { trackWhatsAppClick } from '../lib/analytics';
import { findBundleId } from '../lib/bundles';
import { parsePriceMXN, formatMXN } from '../lib/utils';
import { useCart } from '../context/CartContext';

const ProductModal = ({ product, open, onClose }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [slide, setSlide] = useState(0);
  const [includePair, setIncludePair] = useState(false);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  // Reset local state when product changes
  useEffect(() => {
    setSlide(0);
    setSelectedSize(null);
    setSelectedVariant(product?.variants?.[0] || null);
    setIncludePair(false);
    setAdded(false);
  }, [product]);

  if (!product) return null;
  const active = selectedVariant || product;
  const d = product.details || {};
  const gallery = active.gallery && active.gallery.length > 0
    ? active.gallery
    : [{ type: 'animation', src: product.animation || active.image, label: 'Vista' }];
  const current = gallery[slide] || gallery[0];
  const pairProduct = product.pairId ? products.find((p) => p.id === product.pairId) : null;
  // Si la pieza + su pareja existen como Conjunto, "a juego" agrega el Conjunto a su precio
  // (antes sumaba las dos piezas sueltas y salía $101 más caro que en /conjuntos).
  const bundleProduct = pairProduct
    ? products.find((p) => p.id === findBundleId(product.id, pairProduct.id)) || null
    : null;
  const piecesTotal = pairProduct ? parsePriceMXN(product.price) + parsePriceMXN(pairProduct.price) : 0;
  const bundleSavings = bundleProduct ? piecesTotal - parsePriceMXN(bundleProduct.price) : 0;
  const shownPrice = includePair && pairProduct
    ? bundleProduct ? bundleProduct.price : `$${formatMXN(piecesTotal)} MXN`
    : product.price;

  const prevSlide = () => setSlide((s) => (s - 1 + gallery.length) % gallery.length);
  const nextSlide = () => setSlide((s) => (s + 1) % gallery.length);

  const waMessage = `Hola QUINLATE, me interesa: ${product.name}${selectedVariant ? ` (${selectedVariant.name})` : ''}${selectedSize ? ` (${selectedSize})` : ''}${includePair && pairProduct ? ` + ${pairProduct.name}${bundleProduct ? ` (${bundleProduct.name}, ${bundleProduct.price})` : ''}` : ''}. ¿Me pueden cotizar?`;

  const handleClose = () => {
    onClose();
  };

  const handleAddToCajita = () => {
    if (includePair && bundleProduct) {
      addItem({
        cartId: bundleProduct.id,
        id: bundleProduct.id,
        name: bundleProduct.name,
        price: bundleProduct.price,
      });
    } else {
      addItem({
        cartId: `${active.id}${selectedSize ? `-${selectedSize}` : ''}`,
        id: active.id,
        name: selectedVariant ? `${product.name} — ${selectedVariant.name}` : product.name,
        price: product.price,
        size: selectedSize,
      });
      if (includePair && pairProduct) {
        addItem({
          cartId: `${pairProduct.id}-pair-${product.id}`,
          id: pairProduct.id,
          name: pairProduct.name,
          price: pairProduct.price,
        });
      }
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="q-modal"
        data-testid="product-modal"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <button
          type="button"
          className="q-modal__close"
          onClick={handleClose}
          data-testid="modal-close-btn"
          aria-label="Cerrar"
        >
          <X size={20} strokeWidth={1.5} />
        </button>

        <div className="q-modal__media">
          <img
            key={current.src}
            src={current.src}
            alt={`${product.name} — ${current.label}`}
            className="q-modal__img"
            data-testid="modal-media"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
          {product.featured && (
            <span className="q-card__badge">
              <Sparkles size={12} strokeWidth={1.5} /> Pieza insignia
            </span>
          )}

          {gallery.length > 1 && (
            <>
              <button
                type="button"
                className="q-gallery__arrow q-gallery__arrow--left"
                onClick={prevSlide}
                data-testid="gallery-prev-btn"
                aria-label="Imagen anterior"
              >
                <ChevronLeft size={22} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                className="q-gallery__arrow q-gallery__arrow--right"
                onClick={nextSlide}
                data-testid="gallery-next-btn"
                aria-label="Imagen siguiente"
              >
                <ChevronRight size={22} strokeWidth={1.5} />
              </button>
              <div className="q-gallery__info">
                <span className="q-gallery__label" data-testid="gallery-label">{current.label}</span>
                <div className="q-gallery__dots">
                  {gallery.map((g, i) => (
                    <button
                      key={g.src}
                      type="button"
                      className={`q-gallery__dot ${i === slide ? 'q-gallery__dot--active' : ''}`}
                      onClick={() => setSlide(i)}
                      data-testid={`gallery-dot-${i}`}
                      aria-label={`Ver ${g.label}`}
                    />
                  ))}
                </div>
                <span className="q-gallery__counter" data-testid="gallery-counter">
                  {slide + 1} / {gallery.length}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="q-modal__body">
          <div className="q-card__meta">{product.material}</div>
          <h2 className="q-modal__name" data-testid="modal-product-name">
            {product.name}{selectedVariant ? ` — ${selectedVariant.name}` : ''}
          </h2>
          <p className="q-modal__desc">{active.description || product.description}</p>

          <div className="q-modal__specs">
            {d.quilates && (
              <div className="q-modal__spec">
                <Gem size={16} strokeWidth={1.3} className="q-modal__spec-icon" />
                <div>
                  <span className="q-modal__spec-label">Quilates</span>
                  <span className="q-modal__spec-value">{d.quilates}</span>
                </div>
              </div>
            )}
            {(active.acabado || d.acabado) && (
              <div className="q-modal__spec">
                <Sparkles size={16} strokeWidth={1.3} className="q-modal__spec-icon" />
                <div>
                  <span className="q-modal__spec-label">Acabado</span>
                  <span className="q-modal__spec-value">{active.acabado || d.acabado}</span>
                </div>
              </div>
            )}
          </div>

          {product.contents && product.contents.length > 0 && (
            <div className="q-modal__contents" data-testid="modal-contents">
              <span className="q-modal__spec-label">Contenido del paquete</span>
              <ul className="q-modal__contents-list">
                {product.contents.map((c) => (
                  <li key={c.name} className="q-modal__content">
                    <img
                      src={c.src}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="q-modal__content-thumb"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <span className="q-modal__content-qty">{c.qty}×</span>
                    <span className="q-modal__content-name">{c.name}</span>
                  </li>
                ))}
              </ul>
              <span className="q-modal__contents-total">
                {product.contents.reduce((n, c) => n + c.qty, 0)} piezas en total
              </span>
            </div>
          )}

          {product.variants && product.variants.length > 0 && (
            <div className="q-modal__sizes">
              <span className="q-modal__spec-label">Modelo</span>
              <div className="q-modal__sizes-row">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`q-size ${selectedVariant?.id === v.id ? 'q-size--active' : ''}`}
                    onClick={() => { setSelectedVariant(v); setSlide(0); }}
                    data-testid={`variant-option-${v.id}`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {d.tallas && d.tallas.length > 0 && (
            <div className="q-modal__sizes">
              <span className="q-modal__spec-label">
                <Ruler size={13} strokeWidth={1.4} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                {product.category === 'gorras' ? 'Talla' : 'Medida'}
              </span>
              <div className="q-modal__sizes-row">
                {d.tallas.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`q-size ${selectedSize === t ? 'q-size--active' : ''}`}
                    onClick={() => setSelectedSize(t)}
                    data-testid={`size-option-${t.replace(/\s/g, '-')}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {pairProduct && (
            <div className="q-modal__pair">
              <button
                type="button"
                className={`q-size ${includePair ? 'q-size--active' : ''}`}
                onClick={() => setIncludePair((v) => !v)}
                data-testid="modal-pair-toggle"
              >
                {includePair ? '✓ ' : '+ '}
                {bundleProduct
                  ? `Llévalo en conjunto con ${pairProduct.name}: ${bundleProduct.price} (ahorras $${formatMXN(bundleSavings)})`
                  : `Agregar ${pairProduct.name} a juego (${pairProduct.price})`}
              </button>
            </div>
          )}

          <div className="q-modal__footer">
            <span className="q-modal__price" data-testid="modal-price">{shownPrice}</span>
            <div className="q-modal__footer-actions">
              <button
                type="button"
                className="q-btn q-btn--ghost q-btn--sm"
                onClick={handleAddToCajita}
                data-testid="modal-add-cart-btn"
              >
                {added ? 'Agregado ✓' : 'Agregar a mi carrito'}
              </button>
              <a
                href={waLink(waMessage)}
                {...waLinkProps}
                className="q-btn q-btn--gold q-btn--wa"
                data-testid="modal-whatsapp-btn"
                aria-label="Cotizar por WhatsApp"
                onClick={() => trackWhatsAppClick('product_modal', { product: product.id })}
              >
                <WhatsAppIcon size={22} />
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
